import React from 'react';
import { 
  CreditCardIcon, 
  BanknotesIcon,
  BuildingLibraryIcon,
  DocumentTextIcon,
  CurrencyDollarIcon,
  TruckIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

const PaymentMethodSelector = ({ 
  selectedMethod, 
  onMethodChange, 
  userRole = 'b2c',
  className = '' 
}) => {
  
  const paymentMethods = [
    {
      id: 'cash_on_delivery',
      label: 'מזומן בזמן האספקה',
      description: 'תשלום במזומן בעת קבלת המוצר',
      icon: TruckIcon,
      available: ['b2c', 'b2b', 'admin'],
      popular: true
    },
    {
      id: 'credit_card',
      label: 'כרטיס אשראי',
      description: 'ויזה, מאסטרקארד, אמריקן אקספרס',
      icon: CreditCardIcon,
      available: ['b2c', 'b2b', 'admin'],
      popular: true
    },
    {
      id: 'bank_transfer',
      label: 'העברה בנקאית',
      description: 'העברה ישירה לחשבון הבנק',
      icon: BuildingLibraryIcon,
      available: ['b2c', 'b2b', 'admin'],
      businessFriendly: true
    },
    {
      id: 'check',
      label: 'צ\'ק',
      description: 'תשלום באמצעות צ\'ק חברה או אישי',
      icon: DocumentTextIcon,
      available: ['b2c', 'b2b', 'admin'],
      businessFriendly: true
    },
    {
      id: 'company_credit',
      label: 'אשראי חברה',
      description: 'תשלום באשראי החברה (ללקוחות מאושרים)',
      icon: BuildingLibraryIcon,
      available: ['b2b'],
      businessFriendly: true
    },
    {
      id: 'installments',
      label: 'תשלומים',
      description: 'תשלום בתשלומים (3-12 תשלומים)',
      icon: ClockIcon,
      available: ['b2c', 'b2b']
    },
    {
      id: 'wire_transfer',
      label: 'העברה בינלאומית',
      description: 'העברה בנקאית בינלאומית (SWIFT)',
      icon: BuildingLibraryIcon,
      available: ['b2b'],
      businessFriendly: true
    },
    {
      id: 'cash',
      label: 'מזומן',
      description: 'תשלום במזומן באיסוף עצמי',
      icon: BanknotesIcon,
      available: ['b2c', 'b2b']
    }
  ];

  // Filter methods based on user role
  const availableMethods = paymentMethods.filter(method => 
    method.available.includes(userRole)
  );

  return (
    <div className={`space-y-4 ${className}`} dir="rtl">
      <h3 className="text-lg font-medium text-gray-900 mb-4">
        בחר אמצעי תשלום
      </h3>
      <p className="text-xs text-gray-500 mb-2">Debug: {availableMethods.length} methods available for role: {userRole}</p>
      
      <div className="space-y-3">
        {availableMethods.map((method) => {
          const Icon = method.icon;
          const isSelected = selectedMethod === method.id;
          
          return (
            <div
              key={method.id}
              className={`relative rounded-lg border p-4 cursor-pointer transition-all duration-200 ${
                isSelected
                  ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-500 ring-opacity-50'
                  : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
              }`}
              onClick={() => onMethodChange(method.id)}
            >
              {/* Selection Radio */}
              <div className="flex items-center">
                <input
                  id={method.id}
                  name="payment_method"
                  type="radio"
                  checked={isSelected}
                  onChange={() => onMethodChange(method.id)}
                  className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <label htmlFor={method.id} className="mr-3 flex-1 cursor-pointer">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Icon className="h-6 w-6 text-gray-600 ml-3" />
                      <div>
                        <div className="flex items-center">
                          <span className="text-sm font-medium text-gray-900">
                            {method.label}
                          </span>
                          {/* Badges */}
                          {method.popular && userRole === 'b2c' && (
                            <span className="mr-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              פופולרי
                            </span>
                          )}
                          {method.businessFriendly && userRole === 'b2b' && (
                            <span className="mr-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              מומלץ לעסקים
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 mt-1">
                          {method.description}
                        </p>
                      </div>
                    </div>
                  </div>
                </label>
              </div>

              {/* Additional Info for Selected Method */}
              {isSelected && (
                <div className="mt-3 pt-3 border-t border-blue-200">
                  <div className="text-xs text-blue-700">
                    {method.id === 'cash_on_delivery' && (
                      <p>💡 שירות הגבייה זמין בכל הארץ. ללא עלות נוספת עד ₪500.</p>
                    )}
                    {method.id === 'credit_card' && (
                      <p>💡 תשלום מאובטח SSL. תתקבל אישור מיידי.</p>
                    )}
                    {method.id === 'bank_transfer' && (
                      <p>💡 פרטי החשבון יישלחו במייל לאחר אישור ההזמנה.</p>
                    )}
                    {method.id === 'check' && (
                      <p>💡 ניתן לשלוח צ\'ק או להביא באיסוף עצמי.</p>
                    )}
                    {method.id === 'company_credit' && (
                      <p>💡 זמין ללקוחות מאושרים עם מסגרת אשראי פעילה.</p>
                    )}
                    {method.id === 'installments' && (
                      <p>💡 אפשרות לתשלומים ללא ריבית (עד 6 חודשים) או בריבית (עד 12 חודשים).</p>
                    )}
                    {method.id === 'wire_transfer' && (
                      <p>💡 מתאים להזמנות בינלאומיות. פרטי SWIFT יישלחו במייל.</p>
                    )}
                    {method.id === 'cash' && (
                      <p>💡 תשלום במזומן במעמד איסוף המוצר ממחסן החברה.</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Payment Info */}
      <div className="mt-6 p-3 bg-gray-50 rounded-md">
        <div className="flex">
          <div className="flex-shrink-0">
            <CurrencyDollarIcon className="h-5 w-5 text-gray-400" />
          </div>
          <div className="mr-3">
            <h4 className="text-sm font-medium text-gray-900">
              תנאי תשלום
            </h4>
            <div className="mt-1 text-sm text-gray-500">
              {userRole === 'b2b' ? (
                <p>• ללקוחות עסקיים זמינים תנאי תשלום של 30 יום<br/>
                • אפשרות לתשלום מקדמה עם הנחה של 3%<br/>
                • מע"מ יתווסף לכל התשלומים</p>
              ) : (
                <p>• כל התשלומים כוללים מע"מ<br/>
                • משלוח חינם מעל ₪300<br/>
                • אפשרות החזרה עד 14 יום</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentMethodSelector;
